import type { ArtifactRecord, ChatMessage } from '../../types'
import {
  isToolResultMessage,
  type ToolResultStatus,
} from '../../utils/extractArtifacts'
import AssistantBubble from './AssistantBubble'
import UserBubble from './UserBubble'

interface MessageItemProps {
  slug: string
  message: ChatMessage
  artifacts?: Record<string, ArtifactRecord>
  toolResults?: Record<string, ToolResultStatus>
  showBootHeader?: boolean
}

function readTokens(usage: unknown, key: string): number {
  if (typeof usage !== 'object' || usage === null) return 0
  const value = (usage as Record<string, unknown>)[key]
  return typeof value === 'number' ? value : 0
}

function totalUsageFor(message: ChatMessage) {
  if (!message.usage) return null
  return {
    inputTokens: readTokens(message.usage, 'input_tokens'),
    outputTokens: readTokens(message.usage, 'output_tokens'),
    cacheCreationInputTokens: readTokens(
      message.usage,
      'cache_creation_input_tokens',
    ),
    cacheReadInputTokens: readTokens(message.usage, 'cache_read_input_tokens'),
    // Server-computed (see ChatMessageSerializer / Lukita::ChatPricing).
    // Defaults to 0 for legacy rows persisted before the field existed —
    // the chip will display $0 in that case, which beats fabricating a
    // number from a client-side pricing copy.
    costMicros: message.costMicros ?? 0,
  }
}

export default function MessageItem({
  slug,
  message,
  artifacts,
  toolResults,
  showBootHeader = false,
}: MessageItemProps) {
  if (message.role === 'user') {
    if (isToolResultMessage(message)) return null

    const text =
      message.contentText ??
      message.contentJson
        .filter((block) => block.type === 'text')
        .map((block) => (block.type === 'text' ? block.text : ''))
        .join('\n')
    if (!text) return null
    const sender = message.mine ? null : (message.senderEmail ?? null)
    return <UserBubble text={text} sender={sender} />
  }

  return (
    <AssistantBubble
      slug={slug}
      blocks={message.contentJson}
      artifacts={artifacts}
      toolResults={toolResults}
      totalUsage={totalUsageFor(message)}
      model={message.model ?? null}
      showBootHeader={showBootHeader}
    />
  )
}
